/**
 * unwrapApiResponse.ts — Normaliza respuestas HTTP de la API en toda la app.
 *
 * Tolera envelopes mal formados (fallback de safeRest, MSW en calentamiento,
 * respuestas legacy) para evitar TypeError en servicios y hooks.
 */

export function unwrapApiList<T>(body: unknown, listKey: string): T[] {
  if (Array.isArray(body)) return body as T[];

  if (!body || typeof body !== "object") return [];

  const envelope = body as { data?: unknown };
  const inner = envelope.data;

  if (Array.isArray(inner)) return inner as T[];

  if (inner && typeof inner === "object") {
    const list = (inner as Record<string, unknown>)[listKey];
    return Array.isArray(list) ? (list as T[]) : [];
  }

  return [];
}

export function unwrapApiPayload<
  T extends Record<string, unknown> = Record<string, unknown>,
>(body: unknown): T {
  if (!body || typeof body !== "object" || Array.isArray(body)) {
    return {} as T;
  }

  const envelope = body as { data?: unknown };
  const inner = envelope.data;

  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as T;
  }

  return {} as T;
}

/** Extrae `data` del envelope con fallback si viene vacío o mal formado. */
export function unwrapApiData<T>(body: unknown, fallback: T): T {
  const payload = unwrapApiPayload(body);
  if (Object.keys(payload).length > 0) return payload as T;
  return fallback;
}

/** Extrae un campo dentro de `data` (p. ej. ventas, cierres, insights). */
export function unwrapApiField<T>(
  body: unknown,
  fieldKey: string,
  fallback: T,
): T {
  const value = unwrapApiPayload(body)[fieldKey];
  if (value === undefined || value === null) return fallback;
  return value as T;
}

/** Entidad única en `data` (POST/PUT/PATCH) con fallback tipado. */
export function unwrapApiEntity<T>(body: unknown, fallback: T): T {
  const payload = unwrapApiPayload(body);
  if (Object.keys(payload).length > 0) return payload as T;
  return fallback;
}
